/* eslint-disable prettier/prettier */
export * from '@salesforce/retail-react-app/app/utils/url'

// OOTB Utils
import {getLocaleByReference, getParamsFromPath, getSiteByReference, getDefaultSite} from '@app/utils/site-utils'
import {urlPartPositions} from '@salesforce/retail-react-app/app/constants'

import {getAppDomain} from '@app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 * Constructs a URL for the product list page based on the given category.
 * The URL path is determined by the category type, which defaults to '/category'.
 * If the category type is 'mosaic', the path '/mosaic' is used instead.
 *
 * @param {Object} category - The category object containing the category ID.
 * @param {string} [categoryType='default'] - The type of category view, which determines the URL path.
 * @returns {string} - The encoded URL string for the product list page.
 */
export const categoryUrlBuilder = (category, categoryType = 'default') => {
    const pathMap = {
        mosaic: '/mosaic',
        default: '/category'
    }
    const basePath = pathMap[categoryType] || pathMap.default

    return encodeURI(`${basePath}/${category.id}`)
}

/**
 * Constructs a URL path with a specified locale, optionally removing certain query parameters
 * and including the default locale if specified.
 *
 * @param {string} shortCode - The locale short code to be used in the URL.
 * @param {Object} [opts={}] - Optional parameters for URL construction.
 * @param {Object} [opts.location=window.location] - The location object containing the current URL.
 * @param {Array<string>} [opts.disallowParams=[]] - An array of query parameter names to be removed from the URL.
 * @param {boolean} [opts.includeDefaultLocale=false] - Whether to include the default locale in the URL.
 * @returns {string} The constructed URL path with the specified locale and optional modifications.
 */
export const getPathWithLocale = (shortCode, opts = {}) => {
    const location = opts.location ? opts.location : window.location
    let {siteRef, localeRef} = getParamsFromPath(`${location.pathname}${location.search}`)
    let {pathname, search} = location

    // sanitize the site from current url if existing
    if (siteRef) {
        pathname = pathname.replace(`/${siteRef}`, '')
        search = search.replace(`site=${siteRef}`, '')
    }
    // sanitize the locale from current url if existing
    if (localeRef) {
        pathname = pathname.replace(`/${localeRef}`, '')
        search = search.replace(`locale=${localeRef}`, '')
    }
    // remove ending any &
    search = search.replace(/&$/, '')

    // Remove query parameters
    const {disallowParams = [], includeDefaultLocale = false} = opts

    let queryString = new URLSearchParams(`${search}`)

    if (disallowParams.length) {
        disallowParams.forEach((param) => {
            queryString.delete(param)
        })
    }

    const site = getSiteByReference(siteRef)
    const locale = getLocaleByReference(site, shortCode)

    const buildUrl = (path, siteAliasOrId, localeAliasOrId) => {
        return `/${siteAliasOrId}/${localeAliasOrId}${path}`
    }

    const localeAliasOrId = locale?.alias || locale?.id

    // rebuild the url with new locale,
    const newUrl = buildUrl(
        `${pathname}${Array.from(queryString).length !== 0 ? `?${queryString}` : ''}`,
        site.alias || site.id,
        localeAliasOrId
    )
    return newUrl
}

/**
 * Constructs a URL with the specified site and locale aliases, preserving the current path minus the existing site and locale segments.
 * @param {Object} location - The location object containing the current path from location = useLocation().
 * @param {Object} site - The current site object containing the alias gtom const {site, locale} = useMultiSite().
 * @param {Object} currentLocale - The current locale object containing the alias from const {site, locale} = useMultiSite().
 * @param {Object} targetLocale - The target locale object containing the alias.
 * @returns {string} The constructed URL with the new site and locale, and the adjusted path.
 */
export const buildUrlWithLocale = (location, site, currentLocale, targetLocale) => {
    const appOrigin = getAppDomain()
    const {app: appConfig} = getConfig()
    const siteAlias = appConfig.siteAliases[site.id]

    const currentPath = location?.pathname
    const hasNoPath = currentPath === '/'
    const localeAlias = targetLocale?.alias

    const pathWithoutSiteAndLocale = currentPath
        .replace(new RegExp(`^/${site.alias}`), '')
        .replace(new RegExp(`^/${currentLocale.alias}`), '')
    const finalPath = hasNoPath ? '/' : pathWithoutSiteAndLocale

    // to build absolute url from appOrigin = getAppDomain() starting from https protocal
    return `${appOrigin}/${siteAlias}/${localeAlias}${finalPath}`
}

/**
* Creates a URL template for building dynamic URLs with variable parameters.
* @param {string} baseUrl - The base URL to use as the template.
* @param {Object} [params] - An optional object containing key-value pairs to be used as URL parameters.
* @returns {string} - A URL template string with placeholders for dynamic values.
*/
export const createUrlTemplate = (appConfig, siteRef, localeRef) => {
    const {site: siteConfig, locale: localeConfig, showDefaults: showDefaultsConfig} = appConfig.url
    const defaultSite = getDefaultSite()
    const site = getSiteByReference(siteRef)
    const defaultLocale = getLocaleByReference(site, site.l10n.defaultLocale)

    const isDefaultSite =
        defaultSite.id === siteRef || (defaultSite.alias && defaultSite.alias === siteRef)
    const isDefaultLocale =
        defaultLocale.id === localeRef || (defaultLocale.alias && defaultLocale.alias === localeRef)

    const querySite =
        (siteConfig === urlPartPositions.QUERY_PARAM && showDefaultsConfig) ||
        (siteConfig === urlPartPositions.QUERY_PARAM && !showDefaultsConfig && !isDefaultSite)
    const queryLocale =
        (localeConfig === urlPartPositions.QUERY_PARAM && showDefaultsConfig) ||
        (localeConfig === urlPartPositions.QUERY_PARAM && !showDefaultsConfig && !isDefaultLocale)

    const isQuery = querySite || queryLocale

    const pathSite =
        (siteConfig === urlPartPositions.PATH && showDefaultsConfig) ||
        (siteConfig === urlPartPositions.PATH && !showDefaultsConfig && !isDefaultSite)
    const pathLocale =
        (localeConfig === urlPartPositions.PATH && showDefaultsConfig) ||
        (localeConfig === urlPartPositions.PATH && !showDefaultsConfig && !isDefaultLocale)

    return (path, site, locale) => {
        const sitePath = pathSite && site ? `/${site}` : ''
        const localePath =
            pathLocale && locale ? `/${locale}` : ''

        const hasQuery = isQuery && (site || locale)
        let queryString = ''
        if (hasQuery) {
            const searchParams = new URLSearchParams()
            querySite && site && searchParams.append('site', site)
            queryLocale && locale && searchParams.append('locale', locale)
            queryString = `?${searchParams.toString()}`
        }
        return `${sitePath}${localePath}${path}${queryString}`
    }
}
